import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Loader2, 
  Download, 
  CheckCircle, 
  Clock, 
  AlertTriangle,
  RefreshCw,
  ExternalLink
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PortalDownload() {
  const { leadId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [downloadStatus, setDownloadStatus] = useState(null);
  const [polling, setPolling] = useState(true);

  const success = searchParams.get("success") === "true";

  useEffect(() => {
    if (success) {
      toast.success("Payment successful! Your website is being built.");
    }
    fetchDownloadStatus();
  }, [leadId, success]);

  useEffect(() => {
    // Poll for updates if still building or awaiting payment confirmation
    if (polling && (downloadStatus?.status === "building" || downloadStatus?.status === "awaiting_payment")) {
      const interval = setInterval(fetchDownloadStatus, 3000); // Poll every 3s for faster UX
      return () => clearInterval(interval);
    }
  }, [polling, downloadStatus?.status]);

  const fetchDownloadStatus = async () => {
    try {
      const response = await axios.get(`${API}/portal/download/${leadId}`, {
        withCredentials: true
      });
      
      const data = response.data;
      
      // Handle the new response format where we always get 200
      if (data.paid === false) {
        // Not paid yet - check if it's a not_found case
        if (data.status === "not_found") {
          setDownloadStatus({ status: "no_order", message: data.message });
          setPolling(false);
        } else {
          // awaiting_payment - keep polling for webhook to complete
          setDownloadStatus({ 
            status: "awaiting_payment", 
            message: data.message,
            business_name: data.business_name 
          });
          // Keep polling for a bit in case webhook hasn't processed yet
        }
      } else {
        // paid: true - update status normally
        setDownloadStatus(data);
        
        // Stop polling if ready or expired
        if (data.status === "ready" || data.status === "expired") {
          setPolling(false);
        }
      }
    } catch (error) {
      console.error("Failed to fetch download status:", error);
      if (error.response?.status === 401) {
        toast.error("Session expired. Please use your magic link again.");
        navigate("/");
      } else if (error.response?.status === 404) {
        setDownloadStatus({ status: "no_order", message: "Build not found" });
        setPolling(false);
      } else {
        toast.error("Failed to load download status");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(`${API}/portal/download/${leadId}/file`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'website.zip';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Download started!");
    } catch (error) {
      console.error("Download failed:", error);
      toast.error(error.response?.data?.detail || "Download failed. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const formatExpiryDate = (isoDate) => {
    if (!isoDate) return "";
    const date = new Date(isoDate);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getTimeRemaining = (expiryDate) => {
    if (!expiryDate) return null;
    const now = new Date();
    const expiry = new Date(expiryDate);
    const diff = expiry - now;
    
    if (diff <= 0) return "Expired";
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) {
      return `${days} day${days !== 1 ? 's' : ''} ${hours} hour${hours !== 1 ? 's' : ''}`;
    }
    return `${hours} hour${hours !== 1 ? 's' : ''} remaining`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="animate-spin mx-auto mb-4" size={40} />
          <p className="text-muted-foreground">Loading your download...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_eff51d80-5789-419f-a2d8-ecfb7c53e41f/artifacts/rtlggz3k_Invigor%20Digital%20logo.png" 
              alt="Invigor Digital" 
              className="h-10 w-auto"
            />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Building State */}
        {downloadStatus?.status === "building" && (
          <Card className="max-w-xl mx-auto">
            <CardContent className="pt-8 pb-8 text-center">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Loader2 size={40} className="text-blue-600 animate-spin" />
              </div>
              <h1 className="text-2xl font-bold mb-2">Building Your Website</h1>
              <p className="text-muted-foreground mb-6">
                We are generating your complete multi-page website. This usually takes 2-5 minutes.
              </p>
              <Progress value={33} className="mb-4" />
              <p className="text-sm text-muted-foreground mb-4">
                Generating pages, optimizing images, creating SEO content...
              </p>
              <Button variant="outline" onClick={fetchDownloadStatus}>
                <RefreshCw size={16} className="mr-2" />
                Check Status
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Awaiting Payment Confirmation State - shows briefly while Stripe webhook processes */}
        {downloadStatus?.status === "awaiting_payment" && (
          <Card className="max-w-xl mx-auto">
            <CardContent className="pt-8 pb-8 text-center">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Loader2 size={40} className="text-blue-600 animate-spin" />
              </div>
              <h1 className="text-2xl font-bold mb-2">Confirming Your Payment</h1>
              {downloadStatus.business_name && (
                <p className="text-muted-foreground mb-2">
                  Website for <strong>{downloadStatus.business_name}</strong>
                </p>
              )}
              <p className="text-muted-foreground mb-6">
                We are verifying your payment with Stripe. This usually takes just a few seconds...
              </p>
              <Progress value={15} className="mb-4" />
              <p className="text-sm text-muted-foreground">
                Please wait while we confirm your payment.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Ready State */}
        {downloadStatus?.status === "ready" && (
          <div className="space-y-6">
            <Card className="max-w-xl mx-auto">
              <CardContent className="pt-8 pb-8 text-center">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle size={40} className="text-green-600" />
                </div>
                <h1 className="text-2xl font-bold mb-2">Your Website is Ready!</h1>
                <p className="text-muted-foreground mb-2">
                  Website for <strong>{downloadStatus.business_name}</strong>
                </p>
                
                <div className="flex items-center justify-center gap-2 text-amber-600 mb-6">
                  <Clock size={18} />
                  <span className="text-sm">
                    Download available for {getTimeRemaining(downloadStatus.expires_at)}
                  </span>
                </div>

                <Button 
                  size="lg" 
                  className="w-full max-w-xs h-14 text-lg"
                  onClick={handleDownload}
                  disabled={downloading}
                  data-testid="portal-download-btn"
                >
                  {downloading ? (
                    <>
                      <Loader2 className="mr-2 animate-spin" size={20} />
                      Downloading...
                    </>
                  ) : (
                    <>
                      <Download className="mr-2" size={20} />
                      Download Website (.zip)
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            <Card className="max-w-xl mx-auto">
              <CardHeader>
                <CardTitle className="text-base">What&apos;s in your download?</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground space-y-2">
                <p>✓ Complete HTML website files</p>
                <p>✓ CSS styles and JavaScript</p>
                <p>✓ Optimized images</p>
                <p>✓ SEO metadata and schema markup</p>
                <p>✓ Mobile-responsive design</p>
                <p>✓ Contact form integration ready</p>
              </CardContent>
            </Card>

            <Card className="max-w-xl mx-auto">
              <CardHeader>
                <CardTitle className="text-base">Need help hosting?</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground">
                <p className="mb-4">
                  If you selected hosting, we will reach out shortly to get your site live.
                  Otherwise, you can upload these files to any web host like:
                </p>
                <div className="flex flex-wrap gap-2">
                  <a href="https://www.netlify.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-blue-600 hover:underline">
                    Netlify <ExternalLink size={12} />
                  </a>
                  <span>•</span>
                  <a href="https://vercel.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-blue-600 hover:underline">
                    Vercel <ExternalLink size={12} />
                  </a>
                  <span>•</span>
                  <a href="https://pages.github.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-blue-600 hover:underline">
                    GitHub Pages <ExternalLink size={12} />
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Expired State */}
        {downloadStatus?.status === "expired" && (
          <Card className="max-w-xl mx-auto">
            <CardContent className="pt-8 pb-8 text-center">
              <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertTriangle size={40} className="text-amber-600" />
              </div>
              <h1 className="text-2xl font-bold mb-2">Download Link Expired</h1>
              <p className="text-muted-foreground mb-6">
                Your download link has expired. Please contact us to get a new link.
              </p>
              <p className="text-sm text-muted-foreground mb-4">
                Expired on: {formatExpiryDate(downloadStatus.expired_at)}
              </p>
              <Button variant="outline" onClick={() => window.location.href = "mailto:info@invigordigital.com"}>
                Contact Support
              </Button>
            </CardContent>
          </Card>
        )}

        {/* No Order State */}
        {downloadStatus?.status === "no_order" && (
          <Card className="max-w-xl mx-auto">
            <CardContent className="pt-8 pb-8 text-center">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertTriangle size={40} className="text-red-600" />
              </div>
              <h1 className="text-2xl font-bold mb-2">No Paid Order Found</h1>
              <p className="text-muted-foreground mb-6">
                {downloadStatus.message || "We could not find a paid order for this build."}
              </p>
              <Button onClick={() => navigate(`/portal/preview/${leadId}`)}>
                Go to Preview
              </Button>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t bg-white/50 mt-16">
        <div className="max-w-4xl mx-auto px-4 py-8 text-center text-sm text-slate-500">
          <p>Questions? Email us at <a href="mailto:info@invigordigital.com" className="text-blue-600 hover:underline">info@invigordigital.com</a></p>
          <p className="mt-2">© {new Date().getFullYear()} Invigor Digital. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
